const DEFAULT_PAYMENT_SETTINGS = Object.freeze({
  requestQuote: true,
  creditCard: true,
});

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

const deepMergePlain = (base = {}, patch = {}) => {
  const out = { ...(isPlainObject(base) ? base : {}) };

  Object.entries(isPlainObject(patch) ? patch : {}).forEach(([key, value]) => {
    if (isPlainObject(value) && isPlainObject(out[key])) {
      out[key] = deepMergePlain(out[key], value);
    } else {
      out[key] = value;
    }
  });

  return out;
};

const normalizeBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "off"].includes(normalized)) return false;

  return Boolean(value);
};

const resolveLegacyRequestQuoteEnabled = (templateConfig = {}, gatewaySettings = {}, payment = {}) => {
  if (payment.method === "credit_card") return false;
  if (payment.method === "request_quote") return true;
  if (gatewaySettings?.requestQuote?.enabled !== undefined) {
    return normalizeBoolean(gatewaySettings.requestQuote.enabled, false);
  }
  if (templateConfig?.quoteGateway?.enabled !== undefined) {
    return normalizeBoolean(templateConfig.quoteGateway.enabled, false);
  }
  return undefined;
};

const resolveLegacyCreditCardEnabled = (_templateConfig = {}, gatewaySettings = {}, payment = {}) => {
  if (payment.method === "credit_card") return true;
  if (payment.method === "request_quote") return false;
  if (gatewaySettings?.stripe?.enabled !== undefined) {
    return normalizeBoolean(gatewaySettings.stripe.enabled, false);
  }
  return undefined;
};

export const normalizePromoStorePaymentGatewayState = (templateConfig = {}) => {
  const cfg = isPlainObject(templateConfig) ? { ...templateConfig } : {};
  const payment = isPlainObject(cfg.payment) ? { ...cfg.payment } : {};
  const addons = isPlainObject(cfg.addons) ? { ...cfg.addons } : {};
  const active = isPlainObject(addons.active) ? { ...addons.active } : {};
  const settings = isPlainObject(addons.settings) ? { ...addons.settings } : {};
  const gatewaySettings = isPlainObject(settings["payment-gateway"])
    ? settings["payment-gateway"]
    : {};

  const legacyRequestQuoteEnabled = resolveLegacyRequestQuoteEnabled(
    cfg,
    gatewaySettings,
    payment,
  );
  const legacyCreditCardEnabled = resolveLegacyCreditCardEnabled(
    cfg,
    gatewaySettings,
    payment,
  );

  const requestQuoteEnabled =
    payment.requestQuote !== undefined
      ? normalizeBoolean(payment.requestQuote, DEFAULT_PAYMENT_SETTINGS.requestQuote)
      : legacyRequestQuoteEnabled !== undefined
        ? legacyRequestQuoteEnabled
        : DEFAULT_PAYMENT_SETTINGS.requestQuote;

  const creditCardEnabled =
    payment.creditCard !== undefined
      ? normalizeBoolean(payment.creditCard, DEFAULT_PAYMENT_SETTINGS.creditCard)
      : legacyCreditCardEnabled !== undefined
        ? legacyCreditCardEnabled
        : DEFAULT_PAYMENT_SETTINGS.creditCard;
  const existingStripe = isPlainObject(gatewaySettings.stripe) ? gatewaySettings.stripe : {};

  cfg.payment = {
    ...payment,
    requestQuote: requestQuoteEnabled,
    creditCard: creditCardEnabled,
  };

  active["payment-gateway"] = requestQuoteEnabled || creditCardEnabled;
  settings["payment-gateway"] = deepMergePlain(gatewaySettings, {
    mode: requestQuoteEnabled ? "request_quote" : creditCardEnabled ? "stripe" : "",
    requestQuote: { enabled: requestQuoteEnabled },
    stripe: { ...existingStripe, enabled: creditCardEnabled },
  });

  cfg.addons = {
    ...addons,
    active,
    settings,
  };

  cfg.quoteGateway = deepMergePlain(
    isPlainObject(cfg.quoteGateway) ? cfg.quoteGateway : {},
    { enabled: requestQuoteEnabled },
  );

  return cfg;
};

export const getPromoStorePaymentSettingsFromTemplateConfig = (templateConfig = {}) =>
  normalizePromoStorePaymentGatewayState(templateConfig).payment || {
    ...DEFAULT_PAYMENT_SETTINGS,
  };
